import React, { useState, useEffect } from "react";
import Step1 from "./Step1";
import SinglePayment from "./SinglePayment";
import Loader from "../img/loader.gif";

import { runTransaction, authenticate, createCustomer, createRecurringPayment, createCard, stateNameToCode } from "../helpers";
import { db, sendEmail } from "../firebase/client";
import { collection, addDoc } from "firebase/firestore";

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const Form = (props) => {
  const techFeePercent = 0.0399;
  const taxRate = 0.0744;

  const [step, setStep] = useState(1);
  const [token, setToken] = useState("");
  const [onlyInternet, setOnlyInternet] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({
    nameOnCard: true,
    creditCardNumber: true,
    expirationMonth: false,
    expirationYear: false,
    cvv: false,
    street: false,
    city: false,
    state: false,
    zip: false,
    phoneNumber: true,
    termsAccepted: true,
  });

  const [step1Fields, setStep1Fields] = useState({
    nameOnCard: "",
    creditCardNumber: "",
    expirationMonth: "Jan",
    expirationYear: "2023",
    cvv: "",
    unitNumber: "",
    street: "4600 E Moody Blvd",
    city: "Bunnell",
    state: "Florida",
    zip: "32110",
    phoneNumber: "",
    email: "",
    amount: 89.99,
    tax: null,
    termsAccepted: false,
    confirmEmail: "",
    techFee: null,
    totalAmount: null,
  });
  const [emailError, setEmailError] = useState(false);
  const [expDateError, setExpDateError] = useState(false);
  const [creditCardError, setCreditCardError] = useState(false);

  const [singlePaymentFields, setSinglePaymentFields] = useState({
    nameOnCard: "",
    creditCardNumber: "",
    expirationMonth: "Jan",
    expirationYear: "2023",
    cvv: "",
    unitNumber: "",
    street: "4600 E Moody Blvd",
    city: "Bunnell",
    state: "Florida",
    zip: "32110",
    phoneNumber: "",
    email: "",
    amount: 89.99,
    tax: null,
    termsAccepted: false,
    confirmEmail: "",
    techFee: null,
    totalAmount: null,
  })

  const handleErrorChange = (field, bool) => {
    setFormErrors((prevState) => {
      const newState = {...prevState};
      newState[field] = bool;
      return newState;
    })
  };

  const goBackFromConfirm = () => {
    setStep1Fields({
      nameOnCard: "",
      creditCardNumber: "",
      expirationMonth: "Jan",
      expirationYear: "2023",
      cvv: "",
      unitNumber: "",
      street: "",
      city: "",
      state: "Alabama",
      zip: "",
      phoneNumber: "",
      email: "",
      amount: 89.99,
      tax: "6.96",
      termsAccepted: false,
      confirmEmail: "",
      techFee: "3.59",
      totalAmount: "100.54",
    });
    setFormErrors({
      nameOnCard: true,
      creditCardNumber: true,
      expirationMonth: false,
      expirationYear: false,
      cvv: true,
      street: false,
      city: false,
      state: false,
      zip: false,
      phoneNumber: true,
      termsAccepted: true,
    });
    setFormSubmitted(false);
  }

  const checkForErrors = () => {
    return !Object.values(formErrors).some((v) => v === true);
  }

  const roundToHundredths = (value) => {
    const parsed = parseFloat(value);
    if (!isFinite(parsed)) {
      return null;
    }
    return parsed.toFixed(2);
  };

  const toTitleCase = (string) => {
    return string.replace(/\w\S*/g, (text) => {
      return text.charAt(0).toUpperCase() + text.substr(1).toLowerCase();
    });
  };

  const checkExpirationDate = (month, year) => {
    const mon = parseInt(month, 10);
    const yr = parseInt(year, 10);
    const today = new Date();
    const thisMonth = today.getMonth();
    const thisYear = today.getFullYear();
    if (thisYear <= yr) {
      if (thisYear === yr) {
        return thisMonth < mon;
      }
      return true;
    }
    return false;
  };

  const handleDate = () => {
   if (step1Fields.expirationMonth && step1Fields.expirationYear) {
     const monthNum = months.indexOf(step1Fields.expirationMonth) + 1;
     let monthStr = monthNum.toString();
     if (monthStr.length === 1) {
       monthStr = "0" + monthStr;
      }
      const yearNum = Math.floor(step1Fields.expirationYear) % 100;
      return monthStr + yearNum.toString();
    }
  };
  handleDate();

  const getEndDate = (date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear() + 1;
    return new Date(`${year}/${month}/${day}`);
  };


  const handleRecurringPayment = async () => {
    const entriesValid = checkForErrors();
    if (entriesValid) {
      setFormLoading(true);

      // Translate fields to necessary data:
      const data = step1Fields;
      const expDate = handleDate();
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);
      const tomorrowISO = tomorrow.toISOString();
      const endDate = getEndDate(today);
      const endDateISO = endDate.toISOString();
      const name = toTitleCase(step1Fields.nameOnCard);
      const address = {
        StreetAddress1: step1Fields.street,
        StreetAddress2: step1Fields.unitNumber.toUpperCase(),
        City: step1Fields.city,
        StateOrProvinceCode: stateNameToCode(step1Fields.state),
        PostalCode: step1Fields.zip,
        CountryCode: "USA"
      };

      // Create a customer:
      const customerResponse = await createCustomer({
        customerInformation: {
          name: name,
          email: step1Fields.email,
          phoneNumber: step1Fields.phoneNumber
        }, token: token, address });

      // Create a card, associate it to the customer created:
      const cardResponse = await createCard(
        {
          authToken: token,
          customerKey: customerResponse.data.CustomerKey,
          expirationDate: expDate,
          cardNumber: data.creditCardNumber
        });

      // Create a recurring payment:
      const recurringPaymentResponse = await createRecurringPayment({
        merchantKey: 205,
        customerKey: customerResponse.data.CustomerKey,
        authToken: token,
        token: cardResponse.data.Token,
        startDate: tomorrowISO,
        endDate: endDateISO,
        amount: Number(data.totalAmount)
      });

      if (recurringPaymentResponse) {
        const toSave = { nextBillDate: recurringPaymentResponse.data.NextBillDate, ...step1Fields };
        console.log(toSave)
        await addDoc(collection(db, "form-data"), toSave);
        await sendEmail({
          emails: step1Fields.email,
          template_id: "d-9160edf4bb774312b74e27ca4527169b"
        });
        setFormLoading(false);
        setFormSubmitted(true);
      }
    } else {
      alert("Please fill out all missing fields");
    }
  }

  const handleSubmit = async () => {
    const data = step1Fields;
    const expDate = handleDate();
    setFormLoading(true);

    const response = await runTransaction({
      CardData: {
        ZipCode: data.zip,
        CardNumber: data.creditCardNumber,
        ExpirationDate: expDate,
        NameOnCard: step1Fields.nameOnCard,
        CVV: step1Fields.cvv
      },
      InvoiceData : {
        TotalAmount: data.totalAmount
      },
      token: token
    });
    if (response) {
      await sendEmail({
        emails: step1Fields.email,
        template_id: "d-9160edf4bb774312b74e27ca4527169b"
      });
      await addDoc(collection(db, "single-payments"), step1Fields);
      setFormLoading(false);
      setFormSubmitted(true);
    }
  }

  useEffect(() => {
    if (step1Fields.amount) {
      setStep1Fields((prev) => {
        const toReturn = { ...prev };
        toReturn.techFee = roundToHundredths(step1Fields.amount * techFeePercent);
        toReturn.tax = roundToHundredths((step1Fields.amount + Number(toReturn.techFee)) * taxRate);
        toReturn.totalAmount = roundToHundredths(Number(toReturn.tax) + Number(toReturn.techFee) + Number(toReturn.amount));
        return toReturn;
      })
    }
  }, [step1Fields.amount]);

  useEffect(() => {
    if (step1Fields.email !== step1Fields.confirmEmail) {
      setEmailError(true);
    } else {
      setEmailError(false);
    }
  }, [step1Fields.email, step1Fields.confirmEmail]);

  useEffect(() => {
    if (step1Fields.expirationMonth && step1Fields.expirationYear) {
      const monthNum = months.indexOf(step1Fields.expirationMonth) + 1;
      if (!checkExpirationDate(monthNum, step1Fields.expirationYear)) {
        setExpDateError(true);
      } else {
        setExpDateError(false);
      }
    }
  }, [step1Fields.expirationMonth, step1Fields.expirationYear]);

  useEffect(() => {
    if (step1Fields.creditCardNumber.length === 0 || step1Fields.creditCardNumber.length === 15 || step1Fields.creditCardNumber.length === 16) {
      setCreditCardError(false);
    } else {
      setCreditCardError(true);
    };
  }, [step1Fields.creditCardNumber]);

  useEffect(() => {
    authenticate()
      .then((token) => {
        setToken(token);
      })
  }, []);

  useEffect(() => {
    if (window.location.href.includes("internet")) {
      setStep1Fields((prevState) => {
        const newState = { ...prevState };
        newState.amount = 69.99;
        return newState;
      });
      setOnlyInternet(true);
    }
  }, [])

  return (
    <div style={{
      color: "white",
      textTransform: "uppercase",
      display: "flex",
      justifyContent: "center",
    }}>
    {formSubmitted ?
      <div style={{
        marginTop: "20%"
      }}>
        <h1>Thank you for signing up with Mi3 Connect!</h1>
        <button
          style={{
            width: "50%",
            height: "36px",
            background: "#83EEFF",
            border: "none",
            borderRadius: "3px",
            fontWeight: 500,
            fontSize: "18px",
            marginTop: "10%"
          }}
          onClick={goBackFromConfirm}
        >
          Go Back
        </button>
      </div> :
      formLoading ?
      <img src={Loader} style={{ width: "20%", marginTop: "25%" }} /> :
      window.location.href.includes("single") ?
      <SinglePayment
        step1Fields={step1Fields}
        setStep1Fields={setStep1Fields}
        emailError={emailError}
        handleSubmit={handleSubmit}
        handleErrorChange={handleErrorChange}
        months={months}
        onlyInternet={onlyInternet}
      /> :
      <Step1
        step1Fields={step1Fields}
        setStep1Fields={setStep1Fields}
        emailError={emailError}
        expDateError={expDateError}
        creditCardError={creditCardError}
        handleSubmit={handleRecurringPayment}
        handleErrorChange={handleErrorChange}
        months={months}
        onlyInternet={onlyInternet}
      />
    }
    </div>
  )
}

export default Form;